import React from 'react';
import { useTranslation } from 'react-i18next';

import type { UserSession } from '@/store/slices/userSessions';

import { Panel, SectionTitle, Button, EmptyState, Skeleton, Text } from '../../../../components';
import { SessionRow } from './SessionRow';
import { groupSessions } from './sessions';
import { List, Divider, Note, Footer, AllDevicesButton, PasswordBody, PasswordCopy } from './AccountSection.styles';

export type AccountSectionProps = {
  sessions: UserSession[];
  /** Token of the session this browser is authenticated with (marks "This device"). */
  currentToken: string | null;
  /** False until the first sessions fetch resolves (drives the skeleton). */
  loaded: boolean;
  error?: string | null;
  /** Token currently being revoked (per-row spinner), or null. */
  revokingToken: string | null;
  revokingAll: boolean;
  /** Revoke every token of a single (non-current) login. */
  onRevoke: (tokens: string[]) => void;
  /** Sign out this device — revokes every token of the current login. */
  onSignOut: (tokens: string[]) => void;
  /** Revoke every session, including this one. */
  onLogoutAll: () => void;
  /** Navigate to the set/change-password flow. */
  onChangePassword: () => void;
  /** Optional card rendered between Password and "Where you're signed in" (the SSO
   *  "Connected accounts" card lives here as a slot, so this component stays
   *  decoupled from SSO, with no social-login import here). */
  connectionsSlot?: React.ReactNode;
};

const SKELETON_ROWS = [0, 1, 2];

/**
 * Account & Sign In. Lists every active login ("device") for the account —
 * device type + name, country (geo-located from the session IP) and when it
 * signed in — with the current browser marked "This device". Expired tokens are
 * dropped and the access+refresh tokens of a single sign-in are collapsed into
 * one row. Each other login can be revoked individually; the footer signs this
 * device out or logs every device out at once.
 */
export const AccountSection = ({
  sessions,
  currentToken,
  loaded,
  error,
  revokingToken,
  revokingAll,
  onRevoke,
  onSignOut,
  onLogoutAll,
  onChangePassword,
  connectionsSlot,
}: AccountSectionProps): React.ReactElement => {
  const { t } = useTranslation();

  // Deduplicated, expiry-filtered, ordered logins (current device first). The
  // live navigator UA pins "This device" to the right row.
  const currentUserAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
  const groups = React.useMemo(
    () => groupSessions(sessions, currentToken, currentUserAgent),
    [sessions, currentToken, currentUserAgent],
  );

  // Tokens of the current login — what "Sign out of this device" revokes (falls
  // back to the bare current token if the current login isn't in the list).
  const currentGroupTokens = React.useMemo(() => {
    const current = groups.find((g) => g.isCurrent);
    if (current) return current.tokens;
    return currentToken ? [currentToken] : [];
  }, [groups, currentToken]);

  const onSignOutClick = React.useCallback(() => onSignOut(currentGroupTokens), [onSignOut, currentGroupTokens]);

  const showList = loaded && !error && groups.length > 0;

  return (
    <>
      <Panel>
        <PasswordBody>
          <PasswordCopy text="Set or change the password you sign in with." tag="span" />
          <Button label={t('Change password')} fullWidth onClick={onChangePassword} />
        </PasswordBody>
      </Panel>

      {connectionsSlot}

      <Panel>
      <SectionTitle
        title={t("Where you're signed in")}
        icon="mobile"
        count={loaded && groups.length > 0 ? String(groups.length) : undefined}
      />

      {!loaded && !error ? (
        <List>
          {SKELETON_ROWS.map((i) => (
            <React.Fragment key={i}>
              {i > 0 ? <Divider /> : null}
              <Skeleton height={42} radius={12} />
            </React.Fragment>
          ))}
        </List>
      ) : null}

      {loaded && error ? <EmptyState text={t("We couldn't load your sessions. Please try again.")} /> : null}

      {loaded && !error && groups.length === 0 ? <EmptyState text={t('You have no active sessions.')} /> : null}

      {showList ? (
        <>
          <List>
            {groups.map((group, index) => (
              <React.Fragment key={group.tokens[0]}>
                {index > 0 ? <Divider /> : null}
                <SessionRow
                  session={group.display}
                  tokens={group.tokens}
                  isCurrent={group.isCurrent}
                  revoking={revokingToken !== null && group.tokens.includes(revokingToken)}
                  onRevoke={onRevoke}
                />
              </React.Fragment>
            ))}
          </List>
          <Note text="Locations are estimated from each device's IP address and may be approximate." tag="span" />
        </>
      ) : null}

      <Footer>
        <Button label={t('Sign out of this device')} fullWidth onClick={onSignOutClick} />
        <AllDevicesButton
          type="button"
          onClick={onLogoutAll}
          disabled={revokingAll}
          aria-busy={revokingAll}
          aria-label={revokingAll ? t('Logging out of all devices…') : t('Log out of all devices')}
        >
          {revokingAll ? <Text text="Logging out…" tag="span" /> : <Text text="Log out of all devices" tag="span" />}
        </AllDevicesButton>
      </Footer>
      </Panel>
    </>
  );
};

export default AccountSection;
